import { useMemo } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useAgentGroups } from '@/features/agent-groups/api';
import { useIvrList } from '@/features/ivr/api';
import { usePromptUploads } from '@/features/voice-campaigns/api';
import { useSmsWebhookList } from '@/features/webhooks/api';
import {
  templateFeatures,
  templateLabel,
  type TtsFlowFormValues,
  type TtsRow,
} from '../../types';

interface Props {
  values: TtsFlowFormValues;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[180px_1fr] gap-3 border-t border-border py-2 text-sm first:border-t-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">
        {value || <span className="text-muted-foreground">—</span>}
      </dd>
    </div>
  );
}

export function StepReview({ values }: Props) {
  const prompts = usePromptUploads();
  const groups = useAgentGroups();
  const smsHooks = useSmsWebhookList();
  const ivrs = useIvrList();
  const features = templateFeatures(values.templateId);

  /** Map prompt ids → label. */
  const promptLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of prompts.data ?? []) {
      map.set(
        String(p.promptId),
        p.fileName || p.promptName || `#${p.promptId}`,
      );
    }
    return (id: string) => map.get(id) ?? (id ? `#${id}` : '');
  }, [prompts.data]);

  /** Group id → name (for patch summary). */
  const groupLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups.data ?? []) map.set(String(g.groupId), g.groupName);
    return (id: string) => map.get(id) ?? id;
  }, [groups.data]);

  void groupLabel; // patch list already carries the friendly name.

  /** SMS webhook lookup — the stored URL field is a JSON string of the
   *  webhook config; match by `url` to find the webhook name. */
  const smsLabel = useMemo(() => {
    return (raw: string): string => {
      if (!raw) return '';
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && 'url' in parsed) {
          const match = (smsHooks.data ?? []).find(
            (w) => (w as { url?: string }).url === parsed.url,
          );
          return match?.webhookName ?? String(parsed.url ?? raw);
        }
      } catch {
        /* not JSON — bare URL */
      }
      return raw;
    };
  }, [smsHooks.data]);

  const ivrLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of ivrs.data ?? []) map.set(String(f.id), f.flowName);
    return (id: string) => map.get(id) ?? (id ? `#${id}` : '');
  }, [ivrs.data]);

  const renderTtsRow = (r: TtsRow, i: number) => (
    <li key={i} className="flex items-baseline gap-2">
      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-bold text-muted-foreground">
        {i + 1}
      </span>
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {r.ttsType}
      </span>
      <span>
        {r.ttsType === 'prompt' ? promptLabel(r.value) : `Slot ${r.value}`}
      </span>
      {r.variableType && (
        <span className="text-xs text-muted-foreground">· {r.variableType}</span>
      )}
    </li>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Review</CardTitle>
        <CardDescription>
          Confirm the flow before saving. Click any earlier step to edit.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="rounded-md border border-border bg-card px-3 py-1">
          <Row label="Name" value={values.ttsFlowName} />
          <Row
            label="Template"
            value={values.templateId ? templateLabel(values.templateId) : ''}
          />
          <Row label="Language" value={values.language} />
          <Row label="Voice" value={values.gender} />
          <Row
            label="TTS rows"
            value={
              values.ttsRows.length === 0 ? (
                ''
              ) : (
                <ol className="space-y-0.5">
                  {values.ttsRows.map((r, i) => renderTtsRow(r, i))}
                </ol>
              )
            }
          />
          {features.hasVoiceFiles && (
            <>
              <Row label="Menu prompt" value={promptLabel(values.menuId)} />
              {values.useWelcomeFile && (
                <Row label="Welcome" value={promptLabel(values.welcomeId)} />
              )}
              {values.useNoinputFile && (
                <Row label="No input" value={promptLabel(values.noInputId)} />
              )}
              {values.useWronginputFile && (
                <Row
                  label="Wrong input"
                  value={promptLabel(values.wrongInputId)}
                />
              )}
              {values.useThanksFile && (
                <Row label="Thanks" value={promptLabel(values.thanksId)} />
              )}
              {features.needsNoAgent && values.useNoAgentFile && (
                <Row
                  label="No agent"
                  value={promptLabel(values.noAgentId)}
                />
              )}
              <Row
                label="Menu wait / re-prompt"
                value={`${values.menuWaitTime || '—'}s / ${values.rePrompt || '—'}`}
              />
            </>
          )}
          {features.hasDtmf && values.templateId === '8' && (
            <Row
              label="Accepted DTMF"
              value={values.dtmf.length ? values.dtmf.join(', ') : ''}
            />
          )}
          {features.hasPatch && (
            <Row
              label="Patch bindings"
              value={
                values.patchList.length === 0 ? (
                  ''
                ) : (
                  <ul className="space-y-0.5">
                    {values.patchList.map((r) => (
                      <li key={r.agentDtmf} className="flex items-baseline gap-2">
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
                          {r.agentDtmf}
                        </span>
                        {r.groupName}
                        <span className="text-xs text-muted-foreground">
                          #{r.groupId}
                        </span>
                      </li>
                    ))}
                  </ul>
                )
              }
            />
          )}
          <Row
            label="SMS on success"
            value={
              values.sendSmsSuccess
                ? smsLabel(values.smsApiSuccess) || 'enabled'
                : 'off'
            }
          />
          {values.sendSmsSuccess && (
            <Row
              label="Success min duration"
              value={`${values.callDurationSMS}s`}
            />
          )}
          <Row
            label="SMS on failure"
            value={
              values.sendSmsFail
                ? smsLabel(values.smsApiFail) || 'enabled'
                : 'off'
            }
          />
          {features.hasSmsDtmf && (
            <Row
              label="SMS on DTMF"
              value={
                values.sendSmsDtmf
                  ? smsLabel(values.smsApiDtmf) || 'enabled'
                  : 'off'
              }
            />
          )}
          {features.hasPatchSms && (
            <>
              <Row
                label="Patch-success SMS"
                value={
                  values.sendSmsPatchSuccess
                    ? smsLabel(values.callPatchSuccessMessage) || 'enabled'
                    : 'off'
                }
              />
              <Row
                label="Patch-fail SMS"
                value={
                  values.sendSmsPatchFail
                    ? smsLabel(values.callPatchFailMessage) || 'enabled'
                    : 'off'
                }
              />
            </>
          )}
          <Row
            label="Webhook"
            value={values.sendWebhook ? values.webhookId || 'enabled' : 'off'}
          />
          <Row
            label="Linked IVR"
            value={values.useIvr ? ivrLabel(values.ivrId) : 'none'}
          />
          <Row label="Start" value={`${values.startDate} ${values.startTime}`.trim()} />
          <Row label="End" value={`${values.endDate} ${values.endTime}`.trim()} />
        </dl>
      </CardContent>
    </Card>
  );
}
